import {
  SERVICE_ITEM_KINDS,
  formatINR,
} from "@garage/shared";
import { Button, inputClass, Label } from "./ui";

export interface DraftItem {
  kind: string;
  name: string;
  quantity: string;
  unitCost: string;
  amount: string;
  warrantyMonths: string;
}

export const emptyItem = (): DraftItem => ({
  kind: "PART",
  name: "",
  quantity: "1",
  unitCost: "",
  amount: "",
  warrantyMonths: "",
});

const KIND_LABEL: Record<string, string> = {
  PART: "Part",
  CONSUMABLE: "Consumable",
  LABOUR: "Labour",
  ACCESSORY: "Accessory",
  OTHER: "Other",
};

/** Common enough to be worth suggesting; the datalist stays editable. */
const SUGGESTIONS = [
  "Engine oil", "Oil filter", "Air filter", "Cabin filter", "Fuel filter",
  "Brake pads", "Brake fluid", "Coolant", "Battery", "Wiper blades",
  "Spark plugs", "Clutch plate", "Tyre", "Wheel alignment", "Wheel balancing",
  "Chain sprocket", "Drive belt", "AC service",
];

export function lineAmount(item: DraftItem): number {
  if (item.amount) return Number(item.amount) || 0;
  const qty = Number(item.quantity) || 0;
  const unit = Number(item.unitCost) || 0;
  return Math.round(qty * unit * 100) / 100;
}

export function LineItems({
  items,
  onChange,
}: {
  items: DraftItem[];
  onChange: (items: DraftItem[]) => void;
}) {
  const set = (i: number, patch: Partial<DraftItem>) =>
    onChange(items.map((it, n) => (n === i ? { ...it, ...patch } : it)));

  const total = items.reduce((sum, it) => sum + lineAmount(it), 0);

  return (
    <div className="flex flex-col gap-3">
      <datalist id="part-suggestions">
        {SUGGESTIONS.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>

      {items.length === 0 && (
        <p className="text-sm text-ink-3">
          Nothing itemised. That is fine — a wash does not need a parts list.
          Add lines when you want to track what a part costs over time.
        </p>
      )}

      {items.map((item, i) => (
        <div
          key={i}
          className="grid grid-cols-2 gap-2 rounded border border-line p-3 sm:grid-cols-12"
        >
          <div className="sm:col-span-3">
            <Label>Type</Label>
            <select
              className={`${inputClass} mt-1 !py-1.5 text-sm`}
              value={item.kind}
              onChange={(e) => set(i, { kind: e.target.value })}
            >
              {SERVICE_ITEM_KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </div>

          <div className="col-span-2 sm:col-span-4">
            <Label>Item</Label>
            <input
              className={`${inputClass} mt-1 !py-1.5 text-sm`}
              list="part-suggestions"
              value={item.name}
              onChange={(e) => set(i, { name: e.target.value })}
              placeholder="Engine oil"
            />
          </div>

          <div className="sm:col-span-1">
            <Label>Qty</Label>
            <input
              className={`${inputClass} mt-1 !py-1.5 text-sm`}
              value={item.quantity}
              onChange={(e) => set(i, { quantity: e.target.value })}
              inputMode="decimal"
            />
          </div>

          <div className="sm:col-span-2">
            <Label>Unit ₹</Label>
            <input
              className={`${inputClass} mt-1 !py-1.5 text-sm`}
              value={item.unitCost}
              onChange={(e) => set(i, { unitCost: e.target.value })}
              inputMode="decimal"
              placeholder="620"
            />
          </div>

          <div className="flex items-end justify-between gap-2 sm:col-span-2">
            <div className="min-w-0">
              <Label>Amount</Label>
              <p className="mt-1 truncate py-1.5 text-sm tabular-nums">
                {formatINR(lineAmount(item))}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onChange(items.filter((_, n) => n !== i))}
              className="mb-1.5 rounded px-2 py-1 text-ink-3 hover:text-danger focus:outline-none focus-visible:ring-2 focus-visible:ring-signal"
              aria-label={`Remove ${item.name || "line"}`}
            >
              ✕
            </button>
          </div>
        </div>
      ))}

      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          className="!py-1.5 text-sm"
          onClick={() => onChange([...items, emptyItem()])}
        >
          + Add line
        </Button>
        {items.length > 0 && (
          <span className="ml-auto font-mono text-sm text-ink-2 tabular-nums">
            Parts total {formatINR(total)}
          </span>
        )}
      </div>
    </div>
  );
}
